import { HTMLAttributes, useMemo } from "react";

import { nanoid } from "nanoid";
import dynamic from "next/dynamic";

const dynamicComponents = {
  "link-line": dynamic(() => import("src/images/icons/link-line.svg")),
  "arrow-up-line": dynamic(() => import("src/images/icons/arrow-up-line.svg")),
  "arrow-left-line": dynamic(() => import("src/images/icons/arrow-left-line.svg")),
  "alert-fill": dynamic(() => import("src/images/icons/alert-fill.svg")),
  "search-line": dynamic(() => import("src/images/icons/search-line.svg")),
  "tray-fill": dynamic(() => import("src/images/icons/tray-fill.svg")),
  "cart-fill": dynamic(() => import("src/images/icons/cart-fill.svg")),
  "star-line": dynamic(() => import("src/images/icons/star-line.svg")),
  "check-line": dynamic(() => import("src/images/icons/check-line.svg")),
  "close-fill": dynamic(() => import("src/images/icons/close-fill.svg")),
  "arrow-left-s-line": dynamic(() => import("src/images/icons/arrow-left-s-line.svg")),
  "arrow-down-s-line": dynamic(() => import("src/images/icons/arrow-down-s-line.svg")),
  "arrow-drop-right-line": dynamic(
    () => import("src/images/icons/arrow-drop-right-line.svg")
  ),
  "arrow-up-down-line": dynamic(() => import("src/images/icons/arrow-up-down-line.svg")),
  "save-fill": dynamic(() => import("src/images/icons/save-fill.svg")),
  "chat-smile-fill": dynamic(() => import("src/images/icons/chat-smile-fill.svg")),
  "subtract-line": dynamic(() => import("src/images/icons/subtract-line.svg")),
  "add-line": dynamic(() => import("src/images/icons/add-line.svg")),
  "arrow-right-line": dynamic(() => import("src/images/icons/arrow-right-line.svg")),
  "settings-fill": dynamic(() => import("src/images/icons/settings-fill.svg")),
  "menu-line": dynamic(() => import("src/images/icons/menu-line.svg")),
  "trash-bin-fill": dynamic(() => import("src/images/icons/trash-bin-fill.svg")),
  "pencil-fill": dynamic(() => import("src/images/icons/pencil-fill.svg")),
  "star-fill": dynamic(() => import("src/images/icons/star-fill.svg")),
  "knife-fill": dynamic(() => import("src/images/icons/knife-fill.svg")),
  "goblet-fill": dynamic(() => import("src/images/icons/goblet-fill.svg")),
  "store-2-fill": dynamic(() => import("src/images/icons/store-2-fill.svg")),
  "user-2-fill": dynamic(() => import("src/images/icons/user-2-fill.svg")),
  "music-2-fill": dynamic(() => import("src/images/icons/music-2-fill.svg")),
  "user-fill": dynamic(() => import("src/images/icons/user-fill.svg")),
  "message-2-fill": dynamic(() => import("src/images/icons/message-2-fill.svg")),
  "message-3-fill": dynamic(() => import("src/images/icons/message-3-fill.svg")),
  "information-fill": dynamic(() => import("src/images/icons/information-fill.svg")),
  "close-line": dynamic(() => import("src/images/icons/close-line.svg")),
  "devices-fill": dynamic(() => import("src/images/icons/devices-fill.svg")),
  "servers-fill": dynamic(() => import("src/images/icons/servers-fill.svg")),
  "cloud-fill": dynamic(() => import("src/images/icons/cloud-fill.svg")),
  "arrow-left-right-fill": dynamic(
    () => import("src/images/icons/arrow-left-right-fill.svg")
  ),
  "file-list-3-line": dynamic(() => import("src/images/icons/file-list-3-line.svg")),
  "fullscreen-fill": dynamic(() => import("src/images/icons/fullscreen-fill.svg")),
  "refresh-line": dynamic(() => import("src/images/icons/refresh-line.svg")),
  "alert-line": dynamic(() => import("src/images/icons/alert-line.svg")),
  "shut-down-line": dynamic(() => import("src/images/icons/shut-down-line.svg")),
  "emotion-fill": dynamic(() => import("src/images/icons/emotion-fill.svg")),
  "question-line": dynamic(() => import("src/images/icons/question-line.svg")),
  "coins-fill": dynamic(() => import("src/images/icons/coins-fill.svg")),
  "download-fill": dynamic(() => import("src/images/icons/download-fill.svg")),
  "translation-fill": dynamic(() => import("src/images/icons/translation-fill.svg")),
};

export type IIcon = keyof typeof dynamicComponents;
export const IconNames = Object.keys(dynamicComponents);

export interface IconProps extends Omit<HTMLAttributes<SVGElement>, "color"> {
  name: IIcon;
  size?: number;
  color?: string;
}

export default function Icon({ name, size, color, className, ...rest }: IconProps) {
  const IconSVG = dynamicComponents[name];
  const key = useMemo(() => nanoid(), []);

  const props = useMemo(() => {
    const style = rest?.style || {};
    const classNames = ["icon"];

    if (className) {
      classNames.push(...className.split(" "));
    }

    if (size) {
      style.width = `${size}px`;
      style.height = `${size}px`;
    }

    if (color) {
      style.fill = color;
    }

    return {
      className: classNames.join(" "),
      style,
    };
  }, [size, color, className]);

  if (!IconSVG) return null;

  return <IconSVG key={key} {...rest} {...props} />;
}
